import { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';

interface Notification {
  id: string;
  action: string;
  agency_name: string;
  actor_email: string | null;
  created_at: string;
}

const actionLabels: Record<string, { label: string; color: string }> = {
  agency_created: { label: 'New Agency', color: 'bg-green-500' },
  agency_approved: { label: 'Agency Approved', color: 'bg-blue-500' },
  agency_suspended: { label: 'Agency Suspended', color: 'bg-amber-500' },
  agency_deleted: { label: 'Agency Deleted', color: 'bg-red-500' },
  supervisor_added: { label: 'Supervisor Added', color: 'bg-purple-500' },
  supervisor_removed: { label: 'Supervisor Removed', color: 'bg-orange-500' },
  patient_registered: { label: 'Patient Registered', color: 'bg-emerald-500' },
};

export function AdminNotificationBell() {
  const { user } = useAdminAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);

  useEffect(() => {
    // Load last viewed timestamp from localStorage
    const stored = localStorage.getItem('admin_notifications_last_viewed');
    if (stored) {
      setLastViewedAt(stored);
    }
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      const { data, error } = await supabase
        .from('agency_audit_logs')
        .select('id, action, agency_name, actor_email, created_at')
        .order('created_at', { ascending: false })
        .limit(20);

      if (!error && data) {
        setNotifications(data);
        
        // Calculate unread count
        if (lastViewedAt) {
          const unread = data.filter(n => new Date(n.created_at) > new Date(lastViewedAt)).length;
          setUnreadCount(unread);
        } else {
          setUnreadCount(data.length > 0 ? Math.min(data.length, 5) : 0);
        }
      }
    };

    fetchNotifications();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('admin-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'agency_audit_logs'
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          setNotifications(prev => [newNotification, ...prev.slice(0, 19)]);
          setUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, lastViewedAt]);

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      // Mark all as read when opening
      const now = new Date().toISOString();
      localStorage.setItem('admin_notifications_last_viewed', now);
      setLastViewedAt(now);
      setUnreadCount(0);
    }
  };

  const getActionInfo = (action: string) => {
    return actionLabels[action] || { label: action.replace(/_/g, ' '), color: 'bg-muted-foreground' };
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-9 w-9 p-0">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs font-medium flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {notifications.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {notifications.length} recent
            </span>
          )}
        </div>
        <ScrollArea className="h-80">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map((notification) => {
                const actionInfo = getActionInfo(notification.action);
                const isNew = lastViewedAt ? new Date(notification.created_at) > new Date(lastViewedAt) : false;
                
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      'px-4 py-3 hover:bg-accent/50 transition-colors',
                      isNew && 'bg-accent/30'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn('w-2 h-2 mt-2 rounded-full flex-shrink-0', actionInfo.color)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">
                          {actionInfo.label}
                        </p>
                        <p className="text-sm text-muted-foreground truncate">
                          {notification.agency_name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          {notification.actor_email && (
                            <span> • {notification.actor_email}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
